import { useState, useEffect } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import { Bell, Send, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

export default function NotificationsPage() {
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchHistory(); }, []);

  const fetchHistory = async () => {
    try { const r = await api.get('/api/admin/notifications'); setHistory(r.data.notifications || []); }
    catch {} finally { setLoading(false); }
  };

  const handleSend = async () => {
    if (!title.trim() || !message.trim()) { toast.error('Fill in title and message'); return; }
    setSending(true);
    try {
      const res = await api.post('/api/admin/notifications/send', { title: title.trim(), message: message.trim(), notification_type: 'announcement' });
      toast.success(`Notification sent to ${res.data.sent_to} players!`);
      setTitle(''); setMessage(''); fetchHistory();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to send'); }
    finally { setSending(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-notifications-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-3"><Bell className="w-6 h-6 text-[#D4AF37]" /> Push Notifications</h1>
          <p className="text-sm text-gray-400 mt-1">Send notifications to all players</p>
        </div>
      </div>

      {/* Send Form */}
      <div className="glass-card rounded-2xl p-6 space-y-4">
        <h3 className="font-semibold text-white">Compose Notification</h3>
        <div>
          <label className="text-xs text-gray-400 mb-1 block">Title</label>
          <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Weekend Bonus" className="bg-[#0B0E14] border-white/10" data-testid="notif-title" />
        </div>
        <div>
          <label className="text-xs text-gray-400 mb-1 block">Message</label>
          <textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="e.g. Deposit now and get 50% bonus this weekend!"
            className="w-full h-24 rounded-xl px-3 py-2 text-sm bg-[#0B0E14] border border-white/10 text-white resize-none focus:outline-none focus:border-[#D4AF37]/50" data-testid="notif-message" />
        </div>
        <Button onClick={handleSend} disabled={sending || !title.trim() || !message.trim()} className="bg-[#D4AF37] text-black font-bold" data-testid="send-notif-btn">
          {sending ? <div className="spinner w-4 h-4" /> : <><Send className="w-4 h-4 mr-2" /> Send to All Players</>}
        </Button>
      </div>

      {/* History */}
      <div className="glass-card rounded-2xl p-6">
        <h3 className="font-semibold text-white mb-4">Sent Notifications</h3>
        {loading ? <div className="flex justify-center py-8"><div className="spinner" /></div> : history.length === 0 ? (
          <p className="text-center text-gray-500 py-8">No notifications sent yet</p>
        ) : (
          <div className="space-y-2">
            {history.map(n => (
              <div key={n.id} className="flex items-start justify-between p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white">{n.title}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{n.message}</p>
                  <p className="text-[10px] text-gray-600 mt-1">Sent to {n.target_count} players - {new Date(n.created_at).toLocaleString()}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
